import {
  IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
  type TEmitterType,
  getValueTypeFromEmitterType,
} from '@semios/app-platform-values-helpers-and-types'
import { irrigationSchedulerEventsGet } from 'App/irrigation-scheduler/utils/api/irrigationSchedulerEventsGet'
import moment from 'moment-timezone'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { apiFetch } from 'utils/apiFetch'
import { captureFrontendError } from 'utils/captureFrontendError'
import { getWeekDateRange } from './getWeekDateRange'
import { splitEtDataByWeek } from './splitEtDataByWeek'
import { transformValuesToYtdData } from './transformValuesToYtdData'

export const fetchIrrigationData = async (selectedProperty: TProperty, timezone: string) => {
  const blockIds = Object.keys(selectedProperty.blocks ?? {}).map(Number)
  const irrigationZoneEmitterIds = Object.keys(selectedProperty.irrigationZoneEmitters ?? {})
  const irrigationZoneIds = [...new Set(irrigationZoneEmitterIds.map((id) => Number(id.split('|||')[0])))]
  const tz = moment.tz(timezone)
  const lastWeekDates = getWeekDateRange(tz, -1)
  const thisWeekDates = getWeekDateRange(tz, 0)
  const nextWeekDates = getWeekDateRange(tz, 1)
  const ytdDateFrom = tz.clone().startOf('year').toISOString()
  const ytdDateTo = tz.clone().toISOString()
  const emitterTypes = [...new Set(irrigationZoneEmitterIds.map((id) => id.split('|||')[1] as TEmitterType))]

  const iaValuesRequested = Object.fromEntries(
    emitterTypes.map((et) => [
      getValueTypeFromEmitterType(IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX, et),
      { preferredAggregationInterval: 'DAILY' as const },
    ]),
  )

  const schedulerPromise = Promise.all([
    irrigationSchedulerEventsGet({
      irrigationZoneIds,
      dateFrom: lastWeekDates.dateFrom,
      dateTo: nextWeekDates.dateTo,
    }),
    apiFetch({
      url: '/irrigation-scheduler/planning-data',
      body: {
        blockIds,
        irrigationZoneIds,
        dateFrom: tz.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        dateTo: tz.clone().add(1, 'week').endOf('isoWeek').add(1, 'day').format('YYYY-MM-DD'),
      },
      params: { timeout: 2 * 60 * 1000 },
    }),
  ])

  const ytdPromise = apiFetch({
    url: '/values',
    body: {
      dateFrom: ytdDateFrom,
      dateTo: ytdDateTo,
      featureReferer: 'DASHBOARD' as const,
      blocks: {
        blockIds,
        valuesRequested: {
          evapotranspirationETc: { preferredAggregationInterval: 'DAILY' as const },
        },
      },
      irrigationZones: {
        irrigationZoneIds,
        valuesRequested: iaValuesRequested,
      },
    },
    params: { timeout: 2 * 60 * 1000 },
  })

  const [eventsResponse, planningResponse] = await schedulerPromise

  const { dailyEtLastWeek, dailyEtNextWeek } = splitEtDataByWeek(
    planningResponse?.dailyEt ?? [],
    thisWeekDates,
    nextWeekDates,
    timezone,
  )

  const irrigationData = {
    eventsResponse,
    thisWeekDates,
    nextWeekDates,
    dailyEtNextWeek,
    dailyEtLastWeek,
  }

  const zoneBlockMappings = (planningResponse?.dailyEt ?? []).map(
    (zone: { zone_id: number; blockData: { block_ids: number[] } }) => ({
      zone_id: zone.zone_id,
      block_ids: zone.blockData.block_ids,
    }),
  )

  const fetchYtdData = async () => {
    const ytdResponse = await ytdPromise.catch((err) => {
      captureFrontendError(err, { extra: { message: 'YTD fetch failed' } })
    })

    if (!ytdResponse) return []

    return transformValuesToYtdData({
      etcResponse: ytdResponse,
      iaResponse: ytdResponse,
      zoneBlockMappings,
      emitterTypes,
    })
  }

  return { irrigationData, fetchYtdData }
}
